import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, allCategoriesMap } from '@/mocks/categories';
import { comics, getComicsByCategory } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

type SortKey = 'latest' | 'rating' | 'popular';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<SortKey>('latest');
  const [showMobileFilter, setShowMobileFilter] = useState(false);

  const isAll = slug === 'all';
  const categoryName = isAll ? '全部漫画' : (allCategoriesMap[slug || ''] || '未知分类');
  const currentCategory = categories.find((c) => c.slug === slug);

  const filteredComics: Comic[] = useMemo(() => {
    let result = isAll ? [...comics] : getComicsByCategory(slug || '');
    if (sortBy === 'latest') {
      result = [...result].sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    } else if (sortBy === 'rating') {
      result = [...result].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'popular') {
      result = [...result].sort((a, b) => b.views - a.views);
    }
    return result;
  }, [isAll, slug, sortBy]);

  const sortOptions: { key: SortKey; label: string }[] = [
    { key: 'latest', label: '最新更新' },
    { key: 'rating', label: '评分最高' },
    { key: 'popular', label: '人气最高' },
  ];

  const categoryPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 韩漫大全`,
    description: currentCategory
      ? `${currentCategory.description}，韩漫大全为您精选${currentCategory.name}漫画，免费在线观看`
      : `韩漫大全全部漫画资源，好看的韩国漫画和日漫免费在线观看`,
    url: window.location.href,
    isPartOf: {
      '@type': 'WebSite',
      name: '韩漫大全',
      url: window.location.origin,
    },
    inLanguage: 'zh-CN',
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6">
          {/* Breadcrumb */}
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryName },
            ]}
          />
        </div>

        {/* Header */}
        <section className="py-8 md:py-10 px-4 md:px-6 lg:px-8 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
              {categoryName}
            </h1>
            {currentCategory && (
              <p className="text-sm text-foreground-600">{currentCategory.description}</p>
            )}
          </div>
        </section>

        {/* Filter + Content */}
        <section className="py-8 md:py-10 px-4 md:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Sidebar - Desktop */}
              <aside className="hidden lg:block w-56 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-950 mb-4">漫画分类</h3>
                  <ul className="space-y-1">
                    <li>
                      <Link
                        to="/category/all"
                        className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                          isAll
                            ? 'bg-primary-50 text-primary-500 font-semibold'
                            : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                        }`}
                      >
                        全部漫画
                      </Link>
                    </li>
                    {categories.map((cat) => (
                      <li key={cat.slug}>
                        <Link
                          to={`/category/${cat.slug}`}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                            slug === cat.slug
                              ? 'bg-primary-50 text-primary-500 font-semibold'
                              : 'text-foreground-600 hover:bg-background-100 hover:text-foreground-900'
                          }`}
                        >
                          <div className="w-4 h-4 flex items-center justify-center">
                            <i className={`${cat.icon} text-xs`}></i>
                          </div>
                          {cat.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </aside>

              {/* Main Content */}
              <div className="flex-1 min-w-0">
                {/* Sort */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-1">
                    {sortOptions.map((opt) => (
                      <button
                        key={opt.key}
                        onClick={() => setSortBy(opt.key)}
                        className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          sortBy === opt.key
                            ? 'bg-primary-500 text-background-50'
                            : 'text-foreground-600 bg-background-100 hover:bg-background-200/70'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  {/* Mobile filter toggle */}
                  <button
                    className="lg:hidden flex items-center gap-1 px-3 py-1.5 rounded-full text-sm text-foreground-600 bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => setShowMobileFilter(!showMobileFilter)}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-filter-3-line text-xs"></i>
                    </div>
                    分类
                  </button>
                </div>

                {/* Mobile filter panel */}
                {showMobileFilter && (
                  <div className="lg:hidden mb-6 p-4 rounded-lg bg-background-100">
                    <div className="flex flex-wrap gap-2">
                      <Link
                        to="/category/all"
                        onClick={() => setShowMobileFilter(false)}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                          isAll
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-50 text-foreground-600 hover:bg-background-200/70'
                        }`}
                      >
                        全部
                      </Link>
                      {categories.map((cat) => (
                        <Link
                          key={cat.slug}
                          to={`/category/${cat.slug}`}
                          onClick={() => setShowMobileFilter(false)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            slug === cat.slug
                              ? 'bg-primary-500 text-background-50'
                              : 'bg-background-50 text-foreground-600 hover:bg-background-200/70'
                          }`}
                        >
                          {cat.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Comics Grid */}
                {filteredComics.length > 0 ? (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                    {filteredComics.map((comic) => (
                      <ComicCard key={comic.id} comic={comic} size="medium" />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                      <i className="ri-book-2-line text-2xl"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">该分类暂时没有漫画，去看看其他分类吧～</p>
                    <Link
                      to="/category/all"
                      className="inline-block mt-4 px-6 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      查看全部漫画
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(categoryPageSchema) }} />
    </>
  );
}